import {
	AuthenticateWithRedirectCallback,
	ClerkProvider,
	SignedOut,
} from "@clerk/nextjs";
import { dark } from "@clerk/themes";
import type { Appearance, Theme as ThemeClerk } from "@clerk/types";
import { useRouter } from "next/compat/router";
import { useTheme } from "next-themes";
import type { PropsWithChildren } from "react";

import { clerkSignupCopy } from "~/lib/clerk-signup-copy";
import { font } from "~/utils/constants";

interface AuthProviderProps {
	pageProps?: any;
}

const AuthProvider = ({
	children,
	pageProps,
}: PropsWithChildren<AuthProviderProps>) => {
	const { resolvedTheme } = useTheme();

	const clerkAppearance: Appearance<ThemeClerk> = {
		cssLayerName: "clerk",
		baseTheme:
			resolvedTheme === `dark`
				? dark
				: resolvedTheme === `system` &&
						window.matchMedia(`(prefers-color-scheme: dark)`).matches
					? dark
					: "simple",

		variables: {
			colorPrimary: `#0ea5e9`,
			fontFamily: font.style.fontFamily,
			colorBackground: `var(--color-background)`,
			borderRadius: `8px`,
		},

		elements: {
			rootBox: "!w-full !border-none",
			cardBox:
				"!border-none !w-full dark:!shadow-none !m-0 !rounded-none !overflow-visible",
			card: "!rounded-none h-full dark:!border-none !bg-transparent !border-none !p-0 dark:!shadow-none",
			formFieldInput:
				"!px-4 !py-4.5 rounded-md dark:!ring-0 dark:focus:!ring-3 dark:ring-sky-300/30",
			socialButtonsIconButton: "!py-2.5",
			formButtonPrimary: "!py-2",
			buttonArrowIcon: "!hidden",
			footer: "!hidden",
		},

		layout: {
			logoPlacement: "none",
			shimmer: false,

			showOptionalFields: false,

			helpPageUrl: `/support`,
			privacyPageUrl: `/privacy-policy`,
			termsPageUrl: `/terms-of-service`,
			socialButtonsVariant: "iconButton",
		},
	};

	const router = useRouter();

	const hash = router?.asPath.split("#")[1] || "";

	const isSsoCallback =
		hash.startsWith("/sso-callback") || hash.startsWith("sso-callback-error");

	return (
		<ClerkProvider
			dynamic
			appearance={clerkAppearance}
			{...pageProps}
			localization={clerkSignupCopy}
		>
			{children}

			<SignedOut>
				{isSsoCallback && <AuthenticateWithRedirectCallback />}
			</SignedOut>
		</ClerkProvider>
	);
};

export default AuthProvider;
